import React, { useContext, useState } from "react";
import PropType from "prop-types";
import "./SignUpForm.css";

import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_CUSTOMER,
  CREATE_SUBSCRIPTION,
  CREATE_USER,
  WHO_AM_I,
} from "./queries";
import Button from "../../../../Button";
import { AuthContext } from "../../../../../AuthContext";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import LoadingOverlay from "react-loading-overlay";

export default function SignUpForm(props) {
  const elements = useElements();
  const stripe = useStripe();
  const { setUser } = useContext(AuthContext);
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  const [createUser] = useMutation(CREATE_USER);
  const [createCustomer] = useMutation(CREATE_CUSTOMER);
  const [createSubscription] = useMutation(CREATE_SUBSCRIPTION);

  const { loading: whoAmILoading, error, data } = useQuery(WHO_AM_I);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [promoCode, setPromoCode] = useState(null);
  const [termsAgreed, setTermsAgreed] = useState(false);

  const [errorMessage, setErrorMessage] = useState(undefined);

  if (whoAmILoading) return <></>;
  if (error)
    return (
      <>Something went wrong. Please contact support@gupscorner.com for help.</>
    );

  let user = null;

  if (data.whoAmI) {
    user = data.whoAmI;
  }

  if (user && !props.priceId) {
    history.push("/");
  }

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  async function provisionStripe() {
    const priceId = props.priceId;

    createCustomer().then((customerData) => {
      const customerId = customerData.data.createCustomer.customer.id;

      stripe
        .createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement),
        })
        .then((paymentMethodResult) => {
          if (paymentMethodResult.error) {
            setErrorMessage(paymentMethodResult.error.message);
          } else {
            createSubscription({
              variables: {
                customerId: customerId,
                paymentMethodId: paymentMethodResult.paymentMethod.id,
                priceId: priceId,
                promoCode: promoCode,
              },
            })
              .then((subscriptionResult) => {
                if (
                  subscriptionResult.data.createSubscription.subscription
                    .status === "incomplete"
                ) {
                  setErrorMessage(
                    "Could not process payment at this time. Your account has been created, but your card has not been charged. Please try again later."
                  );
                  setLoading(false);
                } else {
                  history.push("/");
                }
              })
              .catch((e) => {
                setLoading(false);
                if (e.graphQLErrors) {
                  setErrorMessage(e.graphQLErrors[0].message);
                } else {
                  setErrorMessage(
                    "Something went wrong. Please try again later or contact us at support@gupscorner.com"
                  );
                }
              });
          }
        });
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!termsAgreed) {
      setErrorMessage(
        "You must agree to the Terms & Conditions and Privacy Policy to register."
      );

      return null;
    }

    setErrorMessage(undefined);

    setLoading(true);

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      setLoading(false);
    } else {
      if (user === null) {
        createUser({
          variables: {
            username: username,
            password: password,
            email: email,
          },
        })
          .then(async (createUserData) => {
            const sessionKey = createUserData.data.createUser.sessionKey;
            Cookies.set("sessionid", sessionKey, { expires: 14 });
            Cookies.set("loggedIn", true, { expires: 14, httpOnly: false });

            const user = createUserData.data.createUser.user;

            setUser(user);

            if (props.priceId) {
              provisionStripe().then();
            } else {
              history.push("/");
            }
          })
          .catch((e) => {
            setLoading(false);
            if (e.graphQLErrors) {
              setErrorMessage(e.graphQLErrors[0].message);
            } else {
              setErrorMessage(
                "Something went wrong. Please try again later or contact us at support@gupscorner.com"
              );
            }
          });
      } else {
        if (props.priceId) {
          provisionStripe().then();
        } else {
          history.push("/");
        }
      }
    }
  }

  function handleInput(event) {
    if (event.error) {
      setErrorMessage(event.error.message);
    } else {
      setErrorMessage(undefined);
    }
  }

  return (
    <LoadingOverlay active={loading} spinner text="Creating account...">
      <div className="signup-container">
        {user === null ? (
          <p>
            Already have an account? <a href={"/login"}>Login here.</a>
          </p>
        ) : (
          <p>
            Logged in as {user.username}. Not you? <a href="/logout">Logout.</a>
          </p>
        )}
        <br />
        <hr />
        <br />
        <form className="signup-form" onSubmit={handleSubmit}>
          <div className="signup-form-row">
            {user === null ? (
              <>
                <label htmlFor="signup-username" className="signup-label">
                  Username
                </label>
                <input
                  id="signup-username"
                  name="signup-username"
                  className="signup-input"
                  placeholder="Enter Username Here"
                  required
                  value={username}
                  type="text"
                  onChange={(event) => {
                    setUsername(event.target.value);
                  }}
                />

                <label htmlFor="signup-email" className="signup-label">
                  Email
                </label>
                <input
                  id="signup-email"
                  name="signup-email"
                  className="signup-input"
                  placeholder="Enter Email Here"
                  required
                  value={email}
                  type="email"
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                />

                <label htmlFor="signup-password" className="signup-label">
                  Password
                </label>
                <input
                  id="signup-password"
                  name="signup-password"
                  className="signup-input"
                  placeholder="Enter Password Here"
                  required
                  value={password}
                  type="password"
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                />

                <label
                  htmlFor="signup-confirm-password"
                  className="signup-label"
                >
                  Confirm Password
                </label>
                <input
                  id="signup-confirm-password"
                  name="signup-confirm-password"
                  className="signup-input"
                  placeholder="Re-Enter Password Here"
                  required
                  value={confirmPassword}
                  type="password"
                  onChange={(event) => {
                    setConfirmPassword(event.target.value);
                  }}
                />
              </>
            ) : null}

            {props.priceId ? (
              <>
                <label htmlFor="signup-card-element" className="signup-label">
                  Credit or debit card
                </label>
                <CardElement
                  id="signup-card-element"
                  options={CARD_ELEMENT_OPTIONS}
                  onChange={handleInput}
                />

                <label htmlFor="promo-code" className="signup-label">
                  Promo Code (Optional)
                </label>
                <input
                  id="signup-promo-code"
                  name="signup-promo-code"
                  className="signup-input"
                  placeholder="Promo Code (Optional)"
                  value={promoCode}
                  type="text"
                  onChange={(event) => {
                    if (event.target.value === "") {
                      setPromoCode(null);
                    } else {
                      setPromoCode(event.target.value);
                    }
                  }}
                />
              </>
            ) : null}

            <br />

            <input
              type="checkbox"
              id="terms-agree"
              name="terms-agree"
              value="true"
              onChange={(e) => setTermsAgreed(e.target.checked)}
            />
            <label htmlFor="terms-agree">
              {" "}
              I agree to the{" "}
              <a href="/terms-and-conditions">Terms &amp; Conditions</a> and the{" "}
              <a href="/privacy">Privacy Policy</a>.
            </label>
            {errorMessage !== undefined ? (
              <p className="signup-error">{errorMessage}</p>
            ) : null}
          </div>
          <br />
          <Button form={true} style={{ width: "300px" }}>
            Register
          </Button>
        </form>
      </div>
    </LoadingOverlay>
  );
}

SignUpForm.propTypes = {
  updateError: PropType.func.isRequired,
  priceId: PropType.string.isRequired,
};
